import { foundation } from '../../../ui';
import { dataRamp, homeEdge } from '../../../ui/theme/semanticTokens';

/**
 * Home dashboards (KAN-946) — design tokens for the shared pieces.
 *
 * homeDashboard.css / homeBands.css carry NO colour literals: every colour and
 * shadow reaches them as a CSS custom property that `homeCssVars()` builds from
 * the app's design tokens. The few mockup-only values with no token live in
 * HOME_LITERALS below — this file is the ONLY place a hex/rgba literal is allowed.
 */

const fc = foundation.color;

/** Mockup-only shadows with no design token (RD-DASH-011 premium pass). Colours come from `dataRamp` / `homeEdge`. */
export const HOME_LITERALS = Object.freeze({
  cardShadow: '0 1px 2px rgba(16, 24, 40, 0.04), 0 10px 28px -14px rgba(16, 24, 40, 0.12)',
  btnPrimaryShadow: 'inset 0 1px 0 rgba(255, 255, 255, 0.14), 0 1px 2px rgba(28, 90, 165, 0.28)',
  btnSecondaryShadow: `inset 0 0 0 1px ${homeEdge.button}, 0 1px 2px rgba(16, 24, 40, 0.04)`,
});

/** Resolved colour strings for SVG fills and inline bar segments (JS-side). */
export const HOME_COLORS = Object.freeze({
  brand: fc.brand.primary,
  brandLight: dataRamp[600],
  brandPale: dataRamp[400],
  green: fc.semantic.successIcon,
  amber: fc.semantic.warning,
  /** The lightest amber step (overdue 1–30 days). */
  amberLight: fc.semantic.warningFaintBorder,
  /** Closed customers / quiet slate segment. */
  slateSoft: fc.neutral.borderStrong,
  red: fc.semantic.danger,
  /** Unpaid segment / empty capsule track. */
  track: dataRamp.track,
  /** The all-zero donut ring. */
  ringTrack: fc.neutral.surfaceMuted,
  /** Platform: the deepest brand tone (paying segment, Office seats, the current month's column). */
  navy: dataRamp[900],
  /** Light brand tint (Workers seats, the other months' columns, the week tiles' load bars). */
  load: dataRamp[400],
  ramp: dataRamp,
});

/** Spec grid: 12 columns on web, a single column under the dock. */
export const HOME_STACK_GAP = Object.freeze({ web: '16px', tablet: '16px', phone: '12px' });

/** 'phone' (<768) | 'tablet' (768–1023) | 'web' (≥1024). Width comes ONLY from the caller's flags. */
export function resolveHomeLayout({ isMobile = false, isTablet = false } = {}) {
  if (isMobile) return 'phone';
  if (isTablet) return 'tablet';
  return 'web';
}

/**
 * The admin phone/tablet body sits INSIDE the admin frame, which already carries the page gutter
 * (16 phone / 20 tablet, KAN-1005/1006), so the body adds NO side padding on either device.
 * ONE constant read by `AdminDashboardMobile` AND the admin loading skeleton.
 */
export const ADMIN_HOME_COMPACT_PADDING = '12px 0 0';
/** Kept for the callers' `(isTablet)` signature; phone and tablet are identical now. */
export const adminHomeCompactPadding = () => ADMIN_HOME_COMPACT_PADDING;

/**
 * Props for the DashboardTopChrome stack (it forwards `...rest` to the
 * DashboardCardStack div): the 12-column grid on web, one column otherwise.
 * `style.gap` stays so `--cvx-stack-gap` is still mirrored. Never add
 * `display`/`flexDirection` inline — the grid lives in homeDashboard.css.
 */
export function homeStackProps(layout) {
  return {
    className: 'cvx-home',
    'data-home-layout': layout,
    style: { gap: HOME_STACK_GAP[layout] || HOME_STACK_GAP.web },
  };
}

/** Donut sizes by layout (half-width web card 176). */
export const HOME_DONUT_SIZE = Object.freeze({ narrow: 176, tablet: 136, phone: 112 });

/** The CSS custom properties every Home* card root carries. */
export function homeCssVars() {
  return {
    '--cvx-home-surface': fc.neutral.surface,
    '--cvx-home-ink': fc.neutral.text,
    '--cvx-home-ink2': fc.neutral.ink2,
    '--cvx-home-body': fc.neutral.textSecondary,
    '--cvx-home-mute': fc.neutral.textMuted,
    '--cvx-home-faint': fc.neutral.iconMuted,
    '--cvx-home-line': homeEdge.hairline,
    '--cvx-home-edge': homeEdge.card,
    '--cvx-home-divider': fc.neutral.border,
    '--cvx-home-soft': fc.neutral.bg,
    '--cvx-home-hover': fc.neutral.bg,
    '--cvx-home-track': dataRamp.track,
    '--cvx-home-track-soft': fc.neutral.surfaceMuted,
    '--cvx-home-outline': homeEdge.button,
    '--cvx-home-brand': fc.brand.primary,
    '--cvx-home-brand-text': fc.brand.text,
    '--cvx-home-brand-hover': fc.brand.primaryHover,
    '--cvx-home-brand-t': fc.brand.primaryMuted,
    '--cvx-home-brand-t2': fc.brand.primaryMutedStrong,
    '--cvx-home-brand-edge': fc.brand.primaryMutedBorder,
    '--cvx-home-brand-pale': dataRamp[200],
    '--cvx-home-data-900': dataRamp[900],
    '--cvx-home-data-600': dataRamp[600],
    '--cvx-home-data-400': dataRamp[400],
    '--cvx-home-data-200': dataRamp[200],
    '--cvx-home-on-brand': fc.brand.onPrimary,
    '--cvx-home-green': HOME_COLORS.green,
    '--cvx-home-green-x': fc.semantic.success,
    '--cvx-home-green-t': fc.semantic.successFaint,
    '--cvx-home-amber': HOME_COLORS.amber,
    '--cvx-home-amber-x': fc.semantic.warningText,
    '--cvx-home-amber-t': fc.semantic.warningFaint,
    '--cvx-home-red': HOME_COLORS.red,
    '--cvx-home-red-x': fc.semantic.dangerStrong,
    '--cvx-home-red-t': fc.semantic.dangerFaint,
    '--cvx-home-navy': fc.role.platformRole,
    '--cvx-home-navy-t': fc.roleSoft.platformRole,
    '--cvx-home-gray-x': fc.neutral.textSecondary,
    '--cvx-home-gray-t': fc.neutral.surfaceMuted,
    '--cvx-home-load': dataRamp[400],
    '--cvx-home-shadow': HOME_LITERALS.cardShadow,
    '--cvx-home-btn-shadow': HOME_LITERALS.btnPrimaryShadow,
    '--cvx-home-btn2-shadow': HOME_LITERALS.btnSecondaryShadow,
  };
}

/** Built once — the same object on every card root. */
export const HOME_CSS_VARS = Object.freeze(homeCssVars());

export const HOME_TONES = ['brand', 'green', 'amber', 'red', 'gray', 'navy'];

/** `cvx-home-tone--<tone>` sets the --cvx-home-tone-bg/-fg pair (tiles, pills, lead squares). */
export const homeToneClass = (tone) => `cvx-home-tone--${HOME_TONES.includes(tone) ? tone : 'brand'}`;
